import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Separator,
  confirm,
  toast,
} from "@devhop/ui";
import {
  CopyIcon,
  ExternalLinkIcon,
  EyeIcon,
  GlobeIcon,
  LockIcon,
  Share2Icon,
} from "lucide-react";
import { canTransitionFormStatus } from "@/modules/forms/domain/contracts/forms";
import { getFormExternalPublicLinks } from "@/modules/forms/domain/lib/form-external-public-links";
import type { FormDTO } from "@/modules/forms/presentation/api/types";
import { FormPublicLinkQrBlock } from "@/modules/forms/presentation/ui/builder/FormPublicLinkQrBlock";
import { FormStatusBadge } from "@/modules/forms/presentation/ui/FormStatusBadge";
import { formatDateTime } from "@/shared/lib/date-time";

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("ສຳເນົາແລ້ວ");
  } catch {
    toast.error("ສຳເນົາບໍ່ສຳເລັດ");
  }
}

type FormEditPublishShareSectionProps = {
  form: FormDTO;
  canUpdate: boolean;
  hasResponses: boolean;
  isSaving: boolean;
  isStatusPending: boolean;
  publicHref: string;
  viewerViewHref: string;
  viewerPin: string;
  viewerPinConfirm: string;
  viewerLinkExpiryEnabled: boolean;
  viewerLinkExpiryLocal: string;
  onViewerPinChange: (v: string) => void;
  onViewerPinConfirmChange: (v: string) => void;
  onViewerLinkExpiryEnabledChange: (v: boolean) => void;
  onViewerLinkExpiryLocalChange: (v: string) => void;
  onPatchStatus: (status: "published" | "draft" | "closed") => void;
  onSaveViewerLinkExpiry: () => void;
  onSaveViewerPin: () => void;
};

function LinkCopyRow({
  id,
  label,
  href,
  hint,
  disabled,
  canOpen,
}: {
  id: string;
  label: string;
  href: string;
  hint?: string;
  disabled?: boolean;
  canOpen?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap gap-2">
        <Input
          id={id}
          readOnly
          disabled={disabled}
          className="min-w-[12rem] flex-1 font-mono text-sm"
          value={href}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={!href.length || disabled}
          aria-label="ສຳເນົາ"
          onClick={() => void copyText(href)}
        >
          <CopyIcon className="h-4 w-4" />
        </Button>
        {canOpen && href ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="ເປີດ"
            onClick={() => window.open(href, "_blank", "noopener,noreferrer")}
          >
            <ExternalLinkIcon className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
    </div>
  );
}

export function FormEditPublishShareSection({
  form,
  canUpdate,
  hasResponses,
  isSaving,
  isStatusPending,
  publicHref,
  viewerViewHref,
  viewerPin,
  viewerPinConfirm,
  viewerLinkExpiryEnabled,
  viewerLinkExpiryLocal,
  onViewerPinChange,
  onViewerPinConfirmChange,
  onViewerLinkExpiryEnabledChange,
  onViewerLinkExpiryLocalChange,
  onPatchStatus,
  onSaveViewerLinkExpiry,
  onSaveViewerPin,
}: FormEditPublishShareSectionProps) {
  const showPublish =
    canUpdate &&
    form.status === "draft" &&
    canTransitionFormStatus("draft", "published");
  const showClose =
    canUpdate &&
    form.status !== "closed" &&
    canTransitionFormStatus(form.status, "closed");
  const showRevertToDraft =
    canUpdate &&
    form.status === "published" &&
    canTransitionFormStatus("published", "draft");

  const viewerLinkExpired =
    form.settings?.viewerAccessExpiresAt != null &&
    !Number.isNaN(Date.parse(form.settings.viewerAccessExpiresAt)) &&
    Date.parse(form.settings.viewerAccessExpiresAt) <= Date.now();

  const publicLive = form.status === "published";
  const externalPublicLinks = getFormExternalPublicLinks(form.id);

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-lg">
                <GlobeIcon className="h-5 w-5 text-primary" />
                ເຜີຍແຜ່ຟອມ public
              </CardTitle>
              <CardDescription>
                ຈັດການສະຖານະ ແລະ ລິ້ງໃຫ້ຜູ້ຕອບ
              </CardDescription>
            </div>
            <FormStatusBadge status={form.status} />
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="space-y-5">
          <div className="flex flex-wrap gap-2">
            {showPublish ? (
              <Button
                type="button"
                disabled={isStatusPending}
                onClick={() => onPatchStatus("published")}
              >
                ເປີດຮັບຄຳຕອບ (Publish)
              </Button>
            ) : null}
            {showRevertToDraft ? (
              <Button
                type="button"
                variant="outline"
                disabled={isStatusPending}
                onClick={async () => {
                  const ok = await confirm({
                    title: "ກັບເປັນຮ່າງ",
                    description:
                      "ລິ້ງ public ຈະບໍ່ໃຊ້ງານຈົນກວ່າ publish ໃໝ່ — ຄຳຕອບທີ່ມີຢູ່ບໍ່ຖືກລຶບ",
                    actionText: "ກັບເປັນຮ່າງ",
                  });
                  if (ok) onPatchStatus("draft");
                }}
              >
                ກັບເປັນຮ່າງ
              </Button>
            ) : null}
            {showClose ? (
              <Button
                type="button"
                variant="destructive"
                disabled={isStatusPending}
                onClick={() => onPatchStatus("closed")}
              >
                ປິດຟອມ
              </Button>
            ) : null}
          </div>

          <div
            className={`rounded-lg border p-4 ${publicLive ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20" : "border-dashed bg-muted/30"}`}
          >
            <div className="mb-3 flex items-center gap-2 text-sm">
              {publicLive ? (
                <>
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                    ເປີດຮັບຄຳຕອບ
                  </Badge>
                  <span className="text-muted-foreground">ລິ້ງ public ໃຊ້ງານໄດ້</span>
                </>
              ) : (
                <>
                  <Badge variant="outline">ຍັງບໍ່ເປີດ</Badge>
                  <span className="text-muted-foreground">
                    Publish ກ່ອນຈຶ່ງແຊຣລິ້ງໃຫ້ຜູ້ຕອບ
                  </span>
                </>
              )}
            </div>
            <LinkCopyRow
              id="form-public-url"
              label="ລິ້ງໜ້າ public"
              href={publicHref}
              disabled={!publicLive}
              canOpen={publicLive}
              hint={
                publicLive
                  ? "ແຊຣລິ້ງນີ້ໃຫ້ຜູ້ຕອບຟອມ"
                  : "ຕ້ອງ publish ກ່ອນຈຶ່ງໃຊ້ລິ້ງໄດ້"
              }
            />
            <FormPublicLinkQrBlock
              href={publicHref}
              formId={form.id}
              disabled={!publicLive}
              title="QR — admin.fanglaostudio"
              description="ສະແກນເພື່ອເປີດໜ້າ public ຂອງ Admin · ດາວໂຫຼດ PNG ໄປພິມໄດ້"
            />
          </div>

          {externalPublicLinks.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="font-medium text-sm">ລິ້ງ ແລະ QR ຈາກເວັບທີ່ເຊື່ອມ API</p>
                <p className="text-muted-foreground text-xs">
                  ແຊຣໃຫ້ຜູ້ຕອບຜ່ານເວັບພາກສ່ວນໜ້າ (ບໍ່ແມ່ນໜ້າ /f/ ຂອງ Admin)
                </p>
              </div>
              {externalPublicLinks.map((link) => (
                <div
                  key={link.systemKey}
                  className={`rounded-lg border p-4 ${publicLive ? "bg-background" : "border-dashed bg-muted/30"}`}
                >
                  <p className="mb-3 font-medium text-sm">{link.systemLabel}</p>
                  <LinkCopyRow
                    id={`form-external-url-${link.systemKey}`}
                    label="ລິ້ງໜ້າລົງທະບຽນ"
                    href={link.href}
                    disabled={!publicLive}
                    canOpen={publicLive}
                    hint={
                      publicLive
                        ? link.hint
                        : "ຕ້ອງ publish ກ່ອນຈຶ່ງແຊຣລິ້ງ ແລະ QR ໄດ້"
                    }
                  />
                  <FormPublicLinkQrBlock
                    href={link.href}
                    formId={form.id}
                    disabled={!publicLive}
                    title={`QR — ${link.systemLabel}`}
                    description="ສະແກນເພື່ອເປີດໜ້າລົງທະບຽນໃນເວັບນີ້"
                    downloadFilename={`form-${form.id}-${link.qrFilenameSuffix}-qr.png`}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Share2Icon className="h-5 w-5 text-primary" />
            ແຊຣການເບິ່ງຄຳຕອບ
          </CardTitle>
          <CardDescription>
            PIN + ລິ້ງສຳລັບເບິ່ງຜົນຕອບໂດຍບໍ່ຕ້ອງ login admin
          </CardDescription>
        </CardHeader>
        <Separator />
        <CardContent className="space-y-5">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <EyeIcon className="h-4 w-4 text-muted-foreground" />
            {form.viewerPinConfigured ? (
              <Badge variant="secondary">
                <LockIcon className="mr-1 h-3 w-3" />
                ຕັ້ງ PIN ແລ້ວ
              </Badge>
            ) : (
              <Badge variant="outline">ຍັງບໍ່ຕັ້ງ PIN</Badge>
            )}
            {!hasResponses ? (
              <span className="text-destructive text-xs">
                ຕ້ອງມີຄຳຕອບກ່ອນເປີດໃຫ້ເບິ່ງ
              </span>
            ) : null}
            {viewerLinkExpired ? (
              <Badge variant="destructive">ລິ້ງເບິ່ງຫມົດອາຍຸ</Badge>
            ) : null}
          </div>

          <LinkCopyRow
            id="form-viewer-url"
            label="ລິ້ງເບິ່ງຄຳຕອບ"
            href={viewerViewHref}
            hint="ແຊຣ PIN ແຍກຈາກລິ້ງ — ຢ່າໂພສ PIN ສາທາລະນະ"
          />

          {canUpdate ? (
            <>
              <div className="space-y-3 rounded-lg border border-dashed p-4">
                <p className="font-medium text-sm">ຫມົດອາຍຸລິ້ງ (ທາງເລືອກ)</p>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="form-viewer-link-expiry"
                    checked={viewerLinkExpiryEnabled}
                    onCheckedChange={(v) =>
                      onViewerLinkExpiryEnabledChange(Boolean(v))
                    }
                  />
                  <Label htmlFor="form-viewer-link-expiry" className="font-normal">
                    ກຳນົດວັນຫມົດອາຍຸ
                  </Label>
                </div>
                {viewerLinkExpiryEnabled ? (
                  <Input
                    id="form-viewer-link-expiry-at"
                    type="datetime-local"
                    value={viewerLinkExpiryLocal}
                    onChange={(e) => onViewerLinkExpiryLocalChange(e.target.value)}
                  />
                ) : (
                  <p className="text-muted-foreground text-xs">
                    Session ຫຼັງ PIN = 7 ວັນ · ລິ້ງ URL ບໍ່ຫມົດອາຍຸເວັ້ນແຕ່ຈະຕັ້ງ
                  </p>
                )}
                {form.settings?.viewerAccessExpiresAt ? (
                  <p className="text-muted-foreground text-xs">
                    ຕັ້ງໄວ້: {formatDateTime(form.settings.viewerAccessExpiresAt)}
                  </p>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isSaving}
                  onClick={onSaveViewerLinkExpiry}
                >
                  ບັນທຶກຫມົດອາຍຸ
                </Button>
              </div>

              <div className="space-y-3 rounded-lg border p-4">
                <p className="font-medium text-sm">PIN 6 ຫຼັກ</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="form-viewer-pin">PIN ໃໝ່</Label>
                    <Input
                      id="form-viewer-pin"
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="••••••"
                      value={viewerPin}
                      onChange={(e) =>
                        onViewerPinChange(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="form-viewer-pin2">ຢືນຢັນ PIN</Label>
                    <Input
                      id="form-viewer-pin2"
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="••••••"
                      value={viewerPinConfirm}
                      onChange={(e) =>
                        onViewerPinConfirmChange(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSaving}
                  onClick={onSaveViewerPin}
                >
                  {form.viewerPinConfigured ? "ປ່ຽນ PIN" : "ຕັ້ງ PIN"}
                </Button>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
